import { useState } from 'react';
import { Box, ClickAwayListener, Paper, Popper } from '@mui/material';
import { styled } from '@mui/material/styles';

const MenuContainer = styled('div')({
  width: 210,
  padding: 8,
});

const MenuItem = styled('div')<{ isLastItem?: boolean }>(({ isLastItem }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: isLastItem ? 0 : 16,
  cursor: 'pointer',
  padding: '6px',
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
    borderRadius: '4px',
  },
}));

const IconContainer = styled('div')({
  marginRight: 8,
  display: 'flex',
});

interface HelpMenuItem {
  icon: string;
  label: string;
  ariaLabel: string;
  onClick?: () => void;
}

interface HelpMenuProps {
  id?: string;
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  items: HelpMenuItem[];
}

export const HelpMenu: React.FC<HelpMenuProps> = ({
  id,
  open,
  anchorEl,
  onClose,
  items,
}) => {
  return (
    <Popper
      id={id}
      open={open}
      sx={{ zIndex: 1200 }}
      anchorEl={anchorEl}
      modifiers={[
        { name: 'offset', options: { offset: [0, 10] } },
        { name: 'preventOverflow', options: { boundary: document.body } },
      ]}
      placement='bottom-start'
    >
      <ClickAwayListener onClickAway={onClose}>
        <Paper elevation={3} sx={{ borderRadius: 1, overflow: 'hidden' }}>
          <MenuContainer>
            {items.map((item, index) => (
              <MenuItem
                key={index}
                isLastItem={index === items.length - 1}
                onClick={item.onClick}
              >
                <IconContainer>
                  <img
                    src={item.icon}
                    alt={item.label}
                    style={{ width: 24, height: 20 }}
                    aria-label={item.ariaLabel}
                  />
                </IconContainer>
                <Box>{item.label}</Box>
              </MenuItem>
            ))}
          </MenuContainer>
        </Paper>
      </ClickAwayListener>
    </Popper>
  );
};

// Help Menu Hook
export const useHelpMenu = () => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const isOpen = Boolean(anchorEl);
  const dropdownId = isOpen ? 'help-dropdown' : undefined;

  const openMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const closeMenu = () => {
    setAnchorEl(null);
  };

  return {
    anchorEl,
    isOpen,
    dropdownId,
    openMenu,
    closeMenu,
  };
};
